"use client";

import { FC, Fragment, ReactNode, useState } from "react";
import { Tabs, TabsList, TabsTab, TabsPanel, TabsProps } from "@mantine/core";
import clsx from "clsx";

import HtmlText from "@/sections/common/HtmlText";
import { ITextButton } from "@/components/atoms/buttons/types";
import TButton from "@/components/atoms/buttons/TButton";

// Cross-browser scrollbar-hide for the horizontal tab strip on mobile.
const HIDE_SCROLLBAR =
  "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden";

// Shared typography + image treatment for both halves of a tab panel.
// Identical to the article body so a paragraph inside a tab reads at the
// same size, weight, and leading as the paragraph that introduces the
// tab strip. Previously each HtmlText inside a tab fell back to the
// HtmlText defaults (clamp 14-18px on p + larger blockquote/table) and
// looked like a different document.
const TAB_BODY_CLASS = [
  "font-bricolage-grotesque",
  // Body-level elements all at the same 15→17 clamp
  "[&_p]:!font-bricolage-grotesque [&_p]:!text-[clamp(0.9375rem,0.25vw+0.875rem,1.0625rem)] [&_p]:!leading-[1.75]",
  "[&_li]:!font-bricolage-grotesque [&_li]:!text-[clamp(0.9375rem,0.25vw+0.875rem,1.0625rem)] [&_li]:!leading-[1.7]",
  "[&_blockquote]:!text-[clamp(0.9375rem,0.25vw+0.875rem,1.0625rem)] [&_blockquote_*]:!text-[clamp(0.9375rem,0.25vw+0.875rem,1.0625rem)]",
  "[&_td]:!text-[clamp(0.9375rem,0.25vw+0.875rem,1.0625rem)] [&_th]:!text-[clamp(0.9375rem,0.25vw+0.875rem,1.0625rem)]",
  "[&_figcaption]:!text-[clamp(0.8125rem,0.2vw+0.75rem,0.9375rem)]",
  "[&_strong]:!text-inherit [&_em]:!text-inherit [&_a]:!text-inherit",
  // Headings inside a tab panel are smaller than article-body headings —
  // the tab heading itself acts as the section title above them.
  "[&_h2]:!text-[clamp(1.125rem,0.75vw+0.875rem,1.375rem)] [&_h2]:!font-medium [&_h2]:!leading-snug [&_h2]:!mb-3 [&_h2]:!mt-6",
  "[&_h3]:!text-[clamp(1.0625rem,0.5vw+0.875rem,1.25rem)] [&_h3]:!font-medium [&_h3]:!leading-snug [&_h3]:!mb-2 [&_h3]:!mt-5",
  "[&_h4]:!text-[clamp(1rem,0.25vw+0.9375rem,1.125rem)] [&_h4]:!font-medium [&_h4]:!leading-snug",
  "[&_h5]:!text-[clamp(0.9375rem,0.25vw+0.875rem,1.0625rem)] [&_h5]:!font-medium",
  "[&_h6]:!text-[clamp(0.9375rem,0.25vw+0.875rem,1.0625rem)] [&_h6]:!font-medium",
  // Image treatment — gradient box around every <img>, clickable to zoom.
  "[&_img]:!h-auto [&_img]:!max-w-full [&_img]:!max-h-[480px] [&_img]:object-contain [&_img]:mx-auto [&_img]:rounded-xl [&_img]:border [&_img]:border-primary-100 [&_img]:bg-gradient-to-br [&_img]:from-primary-50/60 [&_img]:to-white [&_img]:p-3 [&_img]:shadow-sm [&_img]:my-4 [&_img]:cursor-zoom-in",
  "[&_figure:has(>img)]:my-4 [&_figure:has(>img)]:rounded-xl [&_figure:has(>img)]:border [&_figure:has(>img)]:border-primary-100/60 [&_figure:has(>img)]:bg-gradient-to-br [&_figure:has(>img)]:from-primary-50/60 [&_figure:has(>img)]:to-white [&_figure:has(>img)]:p-4 [&_figure:has(>img)]:shadow-sm",
  "[&_p:has(>img)]:my-4 [&_p:has(>img)]:rounded-xl [&_p:has(>img)]:border [&_p:has(>img)]:border-primary-100/60 [&_p:has(>img)]:bg-gradient-to-br [&_p:has(>img)]:from-primary-50/60 [&_p:has(>img)]:to-white [&_p:has(>img)]:p-4 [&_p:has(>img)]:shadow-sm",
].join(" ");

// ----------------------------------------------------------------------------

export type ITwoColumnTab = {
  heading?: string;
  description?: ReactNode;
  rightDescription?: ReactNode;
  leftButton?: ITextButton;
  rightButton?: ITextButton;
};

export type ITwoColumnTabProps = Omit<TabsProps, "children"> & {
  tabs?: ITwoColumnTab[];
  tabHeadingClass?: string;
  leftDescriptionClass?: string;
  rightDescriptionClass?: string;
};

const TwoColumnTab: FC<ITwoColumnTabProps> = (props) => {
  const {
    tabs,
    tabHeadingClass,
    leftDescriptionClass,
    rightDescriptionClass,
    ...other
  } = props;

  const [activeTab, setActiveTab] = useState(
    tabs ? tabs[tabs.findIndex((tab) => !!tab.heading)]?.heading : "",
  );

  if (!tabs || tabs.length === 0) return;

  return (
    <Tabs
      radius="xs"
      defaultValue={tabs[tabs.findIndex((tab) => !!tab.heading)]?.heading}
      onChange={(value) => value !== null && setActiveTab(value)}
      {...other}
    >
      {/* Pill tab strip — matches the pattern used in
          PenetrationCasesSection and TextWithTabsSection so the article
          body tabs feel like the same UI system. !w-fit hugs the pills;
          max-w-full + overflow-x-auto handle narrow viewports. */}
      <div className="mb-6">
        <TabsList
          className={clsx(
            "flex !w-fit max-w-full flex-nowrap gap-1.5 overflow-x-auto rounded-full border border-black-100/60 bg-white p-1.5 shadow-sm before:hidden sm:gap-2",
            HIDE_SCROLLBAR,
          )}
        >
          {tabs.map((tab, idx) => (
            <Fragment key={idx}>
              {tab.heading && (
                <TabsTab
                  className={clsx(
                    "shrink-0 cursor-pointer whitespace-nowrap rounded-full !border-0 px-3.5 py-2 font-bricolage-grotesque text-[clamp(0.75rem,0.25vw+0.7rem,1rem)] font-normal transition-all duration-200 sm:px-4 sm:py-2.5 md:px-5 md:py-2.5",
                    tab.heading === activeTab
                      ? "bg-primary-900 text-white shadow-md hover:bg-primary-800"
                      : "text-black-700 hover:bg-primary-50 hover:text-primary-900",
                    tabHeadingClass,
                  )}
                  value={tab.heading}
                >
                  {tab.heading}
                </TabsTab>
              )}
            </Fragment>
          ))}
        </TabsList>
      </div>

      {/* Tab descriptions — single column always.
          Render order: rightDescription (typically a chart/image) FIRST
          as the visual hook, then description (text) BELOW for the
          explanation. Authors keep filling the same Strapi fields; the
          visual order is decoupled from the data shape.
          Images auto-scale via [&_img]:!max-w-full so fixed-pixel-width
          charts from CKEditor don't overflow. */}
      {tabs.map((tab, idx) => (
        <Fragment key={idx}>
          {tab.heading && (
            <TabsPanel
              value={tab.heading}
              className="flex flex-col gap-6"
            >
              {(tab.rightDescription || tab.rightButton) && (
                <div className="flex flex-col gap-4">
                  {tab.rightDescription && (
                    <HtmlText
                      className={clsx(
                        // Shared body typography + image box treatment —
                        // matches the article body so tab content reads
                        // as part of the same document.
                        TAB_BODY_CLASS,
                        rightDescriptionClass,
                      )}
                    >
                      {tab.rightDescription}
                    </HtmlText>
                  )}
                  {tab.rightButton && (
                    <TButton
                      href={tab.rightButton.link}
                      variant={tab.rightButton.variant}
                      id={tab.rightButton.id}
                      radius={tab.rightButton.radius}
                      target={tab.rightButton.target}
                    >
                      {tab.rightButton.text}
                    </TButton>
                  )}
                </div>
              )}

              {(tab.description || tab.leftButton) && (
                <div className="flex flex-col gap-4">
                  {tab.description && (
                    <HtmlText
                      className={clsx(
                        // Shared body typography + image box treatment —
                        // matches the article body so tab content reads
                        // as part of the same document.
                        TAB_BODY_CLASS,
                        leftDescriptionClass,
                      )}
                    >
                      {tab.description}
                    </HtmlText>
                  )}
                  {tab.leftButton && (
                    <TButton
                      href={tab.leftButton.link}
                      variant={tab.leftButton.variant}
                      id={tab.leftButton.id}
                      radius={tab.leftButton.radius}
                      target={tab.leftButton.target}
                    >
                      {tab.leftButton.text}
                    </TButton>
                  )}
                </div>
              )}
            </TabsPanel>
          )}
        </Fragment>
      ))}
    </Tabs>
  );
};

export default TwoColumnTab;
